/* eslint-disable no-underscore-dangle --
   Payment and line ids are Mongo `_id` values surfaced by the API. */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal, Table, Button, Spinner, Badge, Alert, Form,
} from 'react-bootstrap';
import PaymentsService from '../../../Services/PaymentsService';
import thousandSeparator from '../../../Utils/numberFormat';
import { SETTLED_PAYMENT_STATUSES as SETTLED } from '../../../Utils/paymentStatus';

// Every money movement with one counterparty, all time, with the per-booking
// allocation of each payment expanded.
//
// This exists because reconciling an operator meant hand-querying Mongo. Four
// separate errors surfaced that way in one day — a payment whose header and
// line allocations disagreed, two bulk wires re-paying a booking an earlier
// wire had already settled, and the same cash return recorded three times.
// None was visible anywhere in the UI: the drill-down shows bookings, not
// payments, and it is scoped to the reporting window, so a wire covering
// several months only ever appears in part.

// A payment's line allocations must add up to its header amount. When they
// drift, month-end reads the lines while the payment screen reads the header,
// and the two disagree with nothing on screen to explain why.
export function lineSumMismatch(payment) {
  const lines = payment?.lines || [];
  if (!lines.length) return 0;
  const sum = lines.reduce((s, l) => s + (Number(l.gross_amount) || 0), 0);
  const diff = sum - (Number(payment.gross_amount) || 0);
  return Math.abs(diff) < 0.01 ? 0 : diff;
}

// Same counterparty, booking, amount and direction on two settled payments is
// nearly always one event entered twice. Flagged rather than hidden — a
// genuine repeat charge is possible, just rare.
export function findDuplicateGroups(payments) {
  const groups = new Map();
  (payments || []).forEach((p) => {
    if (!SETTLED.has(p.status)) return;
    (p.lines || []).forEach((l) => {
      const key = [l.booking_id, l.gross_amount, p.direction, p.currency].join('|');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({
        paymentId: String(p._id),
        ref: l.booking_reference,
        amount: l.gross_amount,
      });
    });
  });
  const dupes = new Map();
  groups.forEach((entries, key) => {
    const distinctPayments = new Set(entries.map((e) => e.paymentId));
    if (distinctPayments.size > 1) dupes.set(key, entries);
  });
  return dupes;
}

// Payment ids that participate in at least one duplicate group.
export function duplicatePaymentIds(payments) {
  const ids = new Set();
  findDuplicateGroups(payments).forEach((entries) => {
    entries.forEach((e) => ids.add(e.paymentId));
  });
  return ids;
}

const fmtDate = (v) => (v ? String(v).slice(0, 10) : '—');
const fmtDateTime = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '—');

function CounterpartyLedgerModal({ show, onHide, counterparty }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [onlyIssues, setOnlyIssues] = useState(false);

  const load = useCallback(async () => {
    if (!counterparty?.id) return;
    setLoading(true);
    setError('');
    try {
      // No date range: the whole point is to see movements the reporting
      // window hides, including wires that straddle month boundaries.
      const data = await PaymentsService.listForCounterparty(
        counterparty.kind,
        counterparty.id,
      );
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      setPayments([]);
      setError(err.response?.data?.message || err.message || 'Failed to load the ledger');
    } finally {
      setLoading(false);
    }
  }, [counterparty]);

  useEffect(() => {
    if (show) {
      setExpanded(new Set());
      setOnlyIssues(false);
      load();
    }
  }, [show, load]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const dupeIds = duplicatePaymentIds(payments);
  const flagged = payments.filter((p) => lineSumMismatch(p) !== 0 || dupeIds.has(String(p._id)));
  const visible = onlyIssues ? flagged : payments;

  // Net movement per currency — outbound minus inbound, in the currency the
  // money actually moved in. Never summed across currencies.
  const byCurrency = new Map();
  payments.forEach((p) => {
    if (!SETTLED.has(p.status)) return;
    const ccy = p.currency || '';
    const signed = (p.direction === 'out' ? 1 : -1) * (Number(p.gross_amount) || 0);
    byCurrency.set(ccy, (byCurrency.get(ccy) || 0) + signed);
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-book me-2" />
          Ledger —
          {' '}
          {counterparty?.name || ''}
          <Badge bg="secondary" className="ms-2 text-uppercase">{counterparty?.kind}</Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="text-muted small mt-2">Loading every movement…</div>
          </div>
        )}
        {!loading && error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && (
          <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="small">
                <span className="text-muted me-2">
                  {payments.length}
                  {' '}
                  payment
                  {payments.length === 1 ? '' : 's'}
                  , all time
                </span>
                {Array.from(byCurrency.entries()).map(([ccy, net]) => (
                  <Badge bg="light" text="dark" className="border me-1 font-monospace" key={ccy}>
                    {`net ${thousandSeparator(net)} ${ccy}`}
                  </Badge>
                ))}
              </div>
              {flagged.length > 0 && (
                <Form.Check
                  type="switch"
                  id="ledger-only-issues"
                  label={`Only flagged (${flagged.length})`}
                  checked={onlyIssues}
                  onChange={(e) => setOnlyIssues(e.target.checked)}
                />
              )}
            </div>

            {flagged.length > 0 && !onlyIssues && (
              <Alert variant="warning" className="py-2 small">
                <i className="fa fa-triangle-exclamation me-1" />
                {flagged.length}
                {' '}
                payment
                {flagged.length === 1 ? '' : 's'}
                {' '}
                need a look — allocations that don&apos;t add up to the payment
                amount, or the same booking and amount recorded twice.
              </Alert>
            )}

            {payments.length === 0 && (
              <div className="text-muted text-center py-4">No payments recorded.</div>
            )}

            {payments.length > 0 && (
              <Table size="sm" responsive hover className="align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 30 }} aria-label="Expand" />
                    <th>Date</th>
                    <th>Dir</th>
                    <th className="text-end">Amount</th>
                    <th>Status</th>
                    <th className="text-end">Allocated</th>
                    <th>Recorded</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.flatMap((p) => {
                    const id = String(p._id);
                    const mismatch = lineSumMismatch(p);
                    const isDupe = dupeIds.has(id);
                    const lines = p.lines || [];
                    const allocated = lines.reduce((s, l) => s + (Number(l.gross_amount) || 0), 0);
                    const isOpen = expanded.has(id);
                    const rows = [(
                      <tr
                        key={id}
                        onClick={() => toggle(id)}
                        style={{ cursor: 'pointer' }}
                        className={mismatch || isDupe ? 'table-warning' : undefined}
                      >
                        <td><i className={`fa fa-chevron-${isOpen ? 'down' : 'right'} text-muted`} /></td>
                        <td className="font-monospace">{fmtDate(p.occurred_at)}</td>
                        <td>
                          <Badge bg={p.direction === 'out' ? 'danger' : 'success'}>
                            {p.direction === 'out' ? 'OUT' : 'IN'}
                          </Badge>
                        </td>
                        <td className="text-end font-monospace fw-bold">
                          {`${thousandSeparator(p.gross_amount)} ${p.currency || ''}`}
                        </td>
                        <td>
                          <Badge bg={SETTLED.has(p.status) ? 'secondary' : 'light'} text={SETTLED.has(p.status) ? undefined : 'dark'}>
                            {p.status}
                          </Badge>
                        </td>
                        <td className="text-end font-monospace">
                          {`${thousandSeparator(allocated)} (${lines.length})`}
                          {mismatch !== 0 && (
                            <div className="text-danger small fw-bold">
                              <i className="fa fa-triangle-exclamation me-1" />
                              {`${mismatch > 0 ? '+' : ''}${thousandSeparator(mismatch)} vs amount`}
                            </div>
                          )}
                          {isDupe && (
                            <div className="text-danger small fw-bold">
                              <i className="fa fa-clone me-1" />
                              possible duplicate
                            </div>
                          )}
                        </td>
                        <td className="small text-muted">
                          <div>{fmtDateTime(p.recorded_at)}</div>
                          <div>{p.recorded_by || ''}</div>
                        </td>
                        <td className="small text-muted" style={{ maxWidth: 240 }}>{p.notes || ''}</td>
                      </tr>
                    )];
                    if (isOpen) {
                      rows.push(
                        <tr key={`${id}-detail`}>
                          <td colSpan={8} className="bg-body-tertiary">
                            {lines.length === 0 ? (
                              <div className="small text-muted py-2">
                                No per-booking allocation — this payment is attributed to the
                                counterparty as a whole.
                              </div>
                            ) : (
                              <Table size="sm" className="mb-0">
                                <thead>
                                  <tr className="small text-muted">
                                    <th>Booking</th>
                                    <th className="text-end">Allocated</th>
                                    <th>Operator on line</th>
                                    <th>Booking id</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {lines.map((l) => (
                                    <tr key={l._id || `${l.booking_id}-${l.gross_amount}`}>
                                      <td className="font-monospace">{l.booking_reference || '—'}</td>
                                      <td className="text-end font-monospace">
                                        {`${thousandSeparator(l.gross_amount)} ${l.currency || p.currency || ''}`}
                                      </td>
                                      <td className="small text-muted font-monospace">
                                        {l.operator_id ? String(l.operator_id).slice(0, 8) : '—'}
                                      </td>
                                      <td className="small text-muted font-monospace">{String(l.booking_id || '')}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            )}
                            <div className="small text-muted mt-2">
                              {`payment id ${id}`}
                              {p.account_id ? ` · account ${p.account_id}` : ''}
                              {p.parent_payment_id ? ` · offsets ${p.parent_payment_id}` : ''}
                            </div>
                          </td>
                        </tr>,
                      );
                    }
                    return rows;
                  })}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CounterpartyLedgerModal;
